import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHourglassHalf,
  faFolderOpen,
  faMinus,
  faPlus,
  faCaretDown,
} from "@fortawesome/free-solid-svg-icons";
import IconStatusHelper from "components/_Helpers/IconStatusHelper";
import DashboardPassportItemFields from "components/DashboardPassportItem/DashboardPassportItemFields";
import DashboardRadio from "components/DashboardRadio";
import PortalTooltip from "./PortalTooltip/PortalTooltip";
import TooltipContent from "./TooltipContent/TooltipContent";
import styles from "./DashboardPassportItem.module.css";

const DashboardPassportItem = ({
  report,
  columns,
  onContact,
  onSelect,
  onLmClick,
  onReview,
  selected,
  onFileClick,
  statusReq,
}) => {
  const reviewColumnTriggerRef = useRef(null);
  const cpnwColumnTriggerRef = useRef(null);
  const edColumnTriggerRef = useRef(null);
  const hcColumnTriggerRef = useRef(null);
  const bgcColumnTriggerRef = useRef(null);
  const watchColumnTriggerRef = useRef(null);

  const [reviewRejectionText, setReviewRejectionText] = useState(
    report.rejectionContent || ""
  );
  const [reviewSelectedStatus, setReviewSelectedStatus] = useState(
    report[DashboardPassportItemFields.assignmentStatus] || ""
  );
  const [hcSelectedLocation, setHcSelectedLocation] = useState(null);

  const [reviewModalHidden, setReviewModalHidden] = useState(true);
  const [cpnwModalHidden, setCpnwModalHidden] = useState(true);
  const [edModalHidden, setEdModalHidden] = useState(true);
  const [hcModalHidden, setHcModalHidden] = useState(true);
  const [bgcModalHidden, setBgcModalHidden] = useState(true);
  const [watchModalHidden, setWatchModalHidden] = useState(true);

  const initHcLocations = useMemo(() => {
    const locations = (statusReq.Healthcare?.result || [])
      .map((item) => ({
        FacilityId: item.FacilityId,
        FacilityName: item.FacilityName,
      }))
      .filter(
        (value, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              t.FacilityId === value.FacilityId &&
              t.FacilityName === value.FacilityName
          )
      );

    return locations;
  }, [statusReq]);

  // State updates during useMemo run while React is rendering. Keep the
  // selected location synchronized after the location list has been computed.
  useEffect(() => {
    setHcSelectedLocation((currentLocation) => {
      if (initHcLocations.length === 0) return null;

      return initHcLocations.some(
        (location) => location.FacilityId === currentLocation
      )
        ? currentLocation
        : initHcLocations[0].FacilityId;
    });
  }, [initHcLocations]);

  const resultByHcSelectedLocation = useCallback(
    (locationId) => {
      const results = (statusReq.Healthcare?.result || [])
        .filter((item) => item.FacilityId === locationId)
        .flatMap((item) => item.Status);
      return results.includes("Incomplete")
        ? "Incomplete"
        : results.includes("Expiring")
        ? "Expiring"
        : "Complete";
    },
    [statusReq]
  );

  const handlReviewSave = () => {
    onReview(
      report[DashboardPassportItemFields.assignmentId],
      reviewSelectedStatus,
      reviewRejectionText
    );
    setReviewModalHidden(true);
  };

  const getIconAndClassNameFromStatus = (status) => {
    let icon = null;
    let className = null;

    if (status) {
      if (status === "Incomplete") {
        icon = faMinus;
        className = styles.faMinusIcon;
      } else if (status === "Expiring") {
        icon = faHourglassHalf;
        className = styles.faHourGlassIcon;
      } else {
        icon = faPlus;
        className = styles.faPlus;
      }
    }

    return { icon, className };
  };

  const renderColumnStatus = (showAssignmentReview, data) => {
    //Assignment Status column

    const onClose = () => {
      setReviewModalHidden(!reviewModalHidden);
    };

    return showAssignmentReview ? (
      <>
        <a
          ref={reviewColumnTriggerRef}
          onClick={() => setReviewModalHidden(!reviewModalHidden)}
          className={classNames([
            styles.statusContent,
            selected ? styles.selectedStatusContent : "",
            styles[`status${data}`],
          ])}
        >
          {data} <FontAwesomeIcon icon={faCaretDown} size="sm" />
        </a>
        <PortalTooltip
          isHidden={reviewModalHidden}
          triggerRef={reviewColumnTriggerRef}
          onClose={() => onClose()}
        >
          <TooltipContent title="CPNW Module Status">
            <div className={styles.status}>Status:</div>
            <div className={styles.radioGroup}>
              <DashboardRadio
                isSelected={reviewSelectedStatus === "Accepted"}
                onChange={setReviewSelectedStatus}
                value="Accepted"
                label="Accepted"
              />
              <DashboardRadio
                isSelected={reviewSelectedStatus === "Rejected"}
                onChange={setReviewSelectedStatus}
                value="Rejected"
                label="Rejected"
              />
            </div>
            {reviewSelectedStatus === "Rejected" && (
              <>
                <label htmlFor="rejectionArea">Rejection Message:</label>
                <textarea
                  name="rejectionArea"
                  id="rejectionArea"
                  onChange={(e) => setReviewRejectionText(e.target.value)}
                  style={{ marginBottom: "10px" }}
                  className={styles.textareaStyle}
                  value={reviewRejectionText}
                ></textarea>
              </>
            )}
            <div className={styles.alignRight}>
              <button
                className={`${styles.saveButton}`}
                onClick={() => setReviewSelectedStatus("")}
              >
                CLEAR
              </button>
              <button
                className={`${styles.saveButton}`}
                onClick={handlReviewSave}
              >
                SAVE
              </button>
            </div>
          </TooltipContent>
        </PortalTooltip>
      </>
    ) : (
      <></>
    );
  };

  const renderColumnName = (data) => {
    //Name column
    return (
      <a
        onClick={() => onContact(data)}
        className={classNames(
          styles.nameContentMain,
          selected ? styles.selectedNameContent : styles.nameContent
        )}
      >
        {data.name}
      </a>
    );
  };

  const renderColumnAd = (data) => {
    //Additional Documents column

    return (
      <FontAwesomeIcon
        icon={faFolderOpen}
        className={styles.faFolderIcon}
        size="sm"
        onClick={() => onFileClick(data)}
      />
    );
  };
  const renderColumnCpnw = (data) => {
    //CPNW Specific Requirements column

    const { icon, className } = getIconAndClassNameFromStatus(
      statusReq.CPNW?.status
    );

    const onClose = () => {
      setCpnwModalHidden(!cpnwModalHidden);
    };

    return statusReq.CPNW ? (
      <>
        <FontAwesomeIcon
          ref={cpnwColumnTriggerRef}
          icon={icon}
          className={className}
          size="sm"
          onClick={() => onClose()}
          style={{ cursor: "pointer" }}
        />
        <PortalTooltip
          isHidden={cpnwModalHidden}
          triggerRef={cpnwColumnTriggerRef}
          onClose={() => onClose()}
        >
          <TooltipContent title="CPNW Module Status">
            <ul className={styles.moduleList}>
              {statusReq.CPNW?.result.map((req, index) => (
                <li
                  key={index}
                  className={`${styles.moduleItem} ${
                    index % 2 === 0 ? styles.even : styles.odd
                  }`}
                >
                  <span className={styles.moduleName}>{req.Name}</span>
                  <span>{IconStatusHelper(req.Status)}</span>
                </li>
              ))}
            </ul>
          </TooltipContent>
        </PortalTooltip>
      </>
    ) : (
      <></>
    );
  };

  const renderColumnEd = (data) => {
    //Education Site Specific Requirement column

    const { icon, className } = getIconAndClassNameFromStatus(
      statusReq.Education?.status
    );

    const onClose = () => {
      setEdModalHidden(!edModalHidden);
    };

    return statusReq.Education ? (
      <>
        <FontAwesomeIcon
          ref={edColumnTriggerRef}
          icon={icon}
          className={className}
          size="sm"
          onClick={() => onClose()}
          style={{ cursor: "pointer" }}
        />
        <PortalTooltip
          isHidden={edModalHidden}
          triggerRef={edColumnTriggerRef}
          onClose={() => onClose()}
        >
          <TooltipContent title="Education Requirement Status">
            <ul className={styles.moduleList}>
              {statusReq.Education?.result.map((req, index) => (
                <li
                  key={index}
                  className={`${styles.moduleItem} ${
                    index % 2 === 0 ? styles.even : styles.odd
                  }`}
                >
                  <span className={styles.moduleName}>{req.Name}</span>
                  <span>{IconStatusHelper(req.Status)}</span>
                </li>
              ))}
            </ul>
          </TooltipContent>
        </PortalTooltip>
      </>
    ) : (
      <></>
    );
  };

  const renderColumnHc = (data) => {
    //Healthcare Site Specific Requirement column

    const { icon, className } = getIconAndClassNameFromStatus(
      statusReq.Healthcare?.status
    );

    const onClose = () => {
      setHcModalHidden(!hcModalHidden);
    };

    return statusReq.Healthcare ? (
      <>
        <FontAwesomeIcon
          ref={hcColumnTriggerRef}
          icon={icon}
          className={className}
          size="sm"
          onClick={() => onClose()}
          style={{ cursor: "pointer" }}
        />
        <PortalTooltip
          isHidden={hcModalHidden}
          triggerRef={hcColumnTriggerRef}
          onClose={() => onClose()}
        >
          <TooltipContent title="Healthcare Requirement Status">
            {initHcLocations.length > 0 ? (
              <div className={styles.locationContainer}>
                <div className={styles.locationSelector}>
                  {initHcLocations.map((location, index) => (
                    <div key={location.FacilityId}>
                      <div
                        key={location.FacilityId}
                        className={classNames(styles.locationItem, {
                          [styles.selectedLocation]:
                            hcSelectedLocation === location.FacilityId,
                        })}
                        onClick={() => setHcSelectedLocation(location.FacilityId)}
                      >
                        <span className={styles.locationName}>
                          {location.FacilityName}
                        </span>
                      </div>
                      {IconStatusHelper(
                        resultByHcSelectedLocation(location.FacilityId)
                      )}
                      {index < initHcLocations.length - 1 && <span> | </span>}
                    </div>
                  ))}
                </div>
                <div>
                  {hcSelectedLocation && (
                    <ul className={styles.moduleList}>
                      {statusReq.Healthcare?.result
                        .filter((item) => item.FacilityId === hcSelectedLocation)
                        .map((req, index) => (
                          <li
                            key={index}
                            className={`${styles.moduleItem} ${
                              index % 2 === 0 ? styles.even : styles.odd
                            }`}
                          >
                            <span className={styles.moduleName}>
                              {req.Name}
                            </span>
                            <span>{IconStatusHelper(req.Status)}</span>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              </div>
            ) : (
              <ul className={styles.moduleList}>
                {statusReq.Healthcare?.result.map((req, index) => (
                  <li
                    key={index}
                    className={`${styles.moduleItem} ${
                      index % 2 === 0 ? styles.even : styles.odd
                    }`}
                  >
                    <span className={styles.moduleName}>{req.Name}</span>
                    <span>{IconStatusHelper(req.Status)}</span>
                  </li>
                ))}
              </ul>
            )}
          </TooltipContent>
        </PortalTooltip>
      </>
    ) : (
      <></>
    );
  };

  const renderColumnOigOrSam = (data) => {
    //OIG Excluded Provider Check column or SAM Excluded Provider Check column
    return IconStatusHelper(data);
  };

  const renderColumnBgc = (data) => {
    //Background Check column
    const { icon, className } = getIconAndClassNameFromStatus(
      statusReq.BGC?.status
    );

    const onClose = () => {
      setBgcModalHidden(!bgcModalHidden);
    };

    return statusReq.BGC ? (
      <>
        <FontAwesomeIcon
          ref={bgcColumnTriggerRef}
          icon={icon}
          className={className}
          size="sm"
          onClick={() => onClose()}
          style={{ cursor: "pointer" }}
        />
        <PortalTooltip
          isHidden={bgcModalHidden}
          triggerRef={bgcColumnTriggerRef}
          onClose={() => onClose()}
        >
          <TooltipContent title="Background Check Status">
            <ul className={styles.moduleList}>
              {statusReq.BGC?.result.map((req, index) => (
                <li
                  key={index}
                  className={`${styles.moduleItem} ${
                    index % 2 === 0 ? styles.even : styles.odd
                  }`}
                >
                  <span className={styles.moduleName}>{req.Name}</span>
                  <span>{IconStatusHelper(req.Status)}</span>
                </li>
              ))}
            </ul>
          </TooltipContent>
        </PortalTooltip>
      </>
    ) : (
      <></>
    );
  };

  const renderColumnWatch = (data) => {
    //Watch Reports column

    const { icon, className } = getIconAndClassNameFromStatus(
      statusReq.WATCH?.status
    );

    const onClose = () => {
      setWatchModalHidden(!watchModalHidden);
    };

    return statusReq.WATCH ? (
      <>
        <FontAwesomeIcon
          ref={watchColumnTriggerRef}
          icon={icon}
          className={className}
          size="sm"
          onClick={() => onClose()}
          style={{ cursor: "pointer" }}
        />
        <PortalTooltip
          isHidden={watchModalHidden}
          triggerRef={watchColumnTriggerRef}
          onClose={() => onClose()}
        >
          <TooltipContent title="Watch Report Status">
            <ul className={styles.moduleList}>
              {statusReq.WATCH?.result.map((req, index) => (
                <li
                  key={index}
                  className={`${styles.moduleItem} ${
                    index % 2 === 0 ? styles.even : styles.odd
                  }`}
                >
                  <span className={styles.moduleName}>{req.Name}</span>
                  <span>{IconStatusHelper(req.Status)}</span>
                </li>
              ))}
            </ul>
          </TooltipContent>
        </PortalTooltip>
      </>
    ) : (
      <></>
    );
  };

  const renderColumnType = (data) => {
    //Type column
    return <span>{data}</span>;
  };

  const renderColumns = (accessor) => {
    const data = report[accessor];

    switch (accessor) {
      case DashboardPassportItemFields.assignmentStatus:
        return renderColumnStatus(report.ShowAssignmentReview, data);

      case DashboardPassportItemFields.nameOfContact:
        return renderColumnName(data);

      case DashboardPassportItemFields.additionalDocuments:
        return renderColumnAd(data);

      case DashboardPassportItemFields.CPNWSpecificRequirements:
        return renderColumnCpnw(data);

      case DashboardPassportItemFields.educationSiteSpecificRequirements:
        return renderColumnEd(data);

      case DashboardPassportItemFields.healthcareSiteSpecificRequirement:
        return renderColumnHc(data);

      case DashboardPassportItemFields.oig:
        return renderColumnOigOrSam(data);

      case DashboardPassportItemFields.sam:
        return renderColumnOigOrSam(data);

      case DashboardPassportItemFields.backgroundCheck:
        return renderColumnBgc(data);

      case DashboardPassportItemFields.watchReports:
        return renderColumnWatch(data);

      case DashboardPassportItemFields.userContactType:
        return renderColumnType(data);

      default:
        return null;
    }
  };

  return (
    <tr
      key={`contact_${report.id}_assignment_${report.assignmentId}`}
      className={classNames(
        selected ? styles.selectedBackground : "",
        styles.custom_height
      )}
    >
      <td className={styles.checkbox}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onSelect(report.id)}
        />
      </td>
      {columns.map(({ accessor }, index) => {
        return (
          <td key={`column_${accessor}_${index}`} className={styles.tableCell}>
            {renderColumns(accessor)}
          </td>
        );
      })}
    </tr>
  );
};

DashboardPassportItem.propTypes = {
  //Boolean to determine if modal is open
  isOpen: PropTypes.bool,
  //Passed in to make changes to the modals style
  className: PropTypes.string,
};

export default DashboardPassportItem;
